import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Users, Heart } from 'lucide-react';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const images = [
    "/images/WhatsApp Image 2025-08-11 at 10.16.19.jpeg",
    "/images/WhatsApp Image 2025-08-11 at 10.16.19 (1).jpeg",
    "/images/WhatsApp Image 2025-08-11 at 10.16.19 (2).jpeg",
    "/images/WhatsApp Image 2025-08-11 at 10.16.19 (3).jpeg",
    "/images/WhatsApp Image 2025-08-11 at 10.16.20 (1).jpeg",
    "/images/WhatsApp Image 2025-08-11 at 10.16.20 (2).jpeg",
    "/images/WhatsApp Image 2025-08-11 at 10.16.21.jpeg",
    "/images/WhatsApp Image 2025-08-11 at 10.16.21 (2).jpeg",
    "/images/20241219_141616.jpg",
    "/images/20241219_142337.jpg"
  ];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToNextSection = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };



  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image Slider */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-black bg-opacity-40 z-10"></div>
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              transition: 'opacity 1.5s ease-in-out'
            }}
          >
            <img 
              src={image} 
              alt={`סדנת חזון ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      {/* Subtle warm overlay elements */}
      <div className="absolute inset-0 z-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-br from-dream-teal to-dream-warm-brown rounded-full blur-3xl opacity-10"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-br from-dream-pink-beige to-dream-soft-beige rounded-full blur-3xl opacity-10"></div>
      </div>

      <motion.div
        className="container-width section-padding text-center relative z-30"
        variants={containerVariants}
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
      >
        <motion.div variants={itemVariants} className="mb-8">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight font-light drop-shadow-lg">
            <span className="text-dream-soft-beige drop-shadow-lg">לחלום</span>
            <br />
            <span className="text-dream-pink-beige font-normal drop-shadow-lg">בגדול</span>
          </h1>
        </motion.div>

        <motion.div variants={itemVariants} className="mb-8">
          <p className="text-xl md:text-2xl text-white max-w-4xl mx-auto leading-relaxed" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)' }}>
            לוח חזון ככלי להשראה, התכווננות וצמיחה אישית בתוך הארגון
            <br />
            <span className="text-lg md:text-xl text-white block mt-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)' }}>
            הזמנה לסדנה חווייתית שמחברת בין יצירתיות, תודעה והגשמה
            </span>
          </p>
        </motion.div>

        <motion.div variants={itemVariants} className="mb-12">
          <div className="flex flex-wrap justify-center gap-6 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)' }}>
            <div className="flex items-center gap-2">
              <Users className="text-dream-teal" size={20} />
              <span>עבור קבוצות ומוסדות</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-dream-warm-brown rounded-full"></div>
              <span>חוויה ייחודית בארץ</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="text-dream-pink-beige" size={20} />
              <span>מסע אישי בקבוצה</span>
            </div>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-6 justify-center items-center">
          <motion.button
            className="bg-gradient-to-r from-dream-teal to-dream-warm-brown text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-105 flex items-center gap-3"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToContact}
          >
            <Play size={20} fill="currentColor" />
            הזמינו את הסדנה עכשיו
          </motion.button>
          
          <motion.button
            className="backdrop-blur-sm bg-white bg-opacity-10 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-opacity-20 transition-all duration-300 border border-white border-opacity-30"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToNextSection}
          >
            למידע נוסף
          </motion.button>
        </motion.div>

        <motion.div variants={itemVariants} className="mt-8">
          <p className="text-white text-sm" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.5)' }}>
            עם גלית תמר ונילי שטול טראורינג
          </p>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center z-30">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white border-opacity-70 rounded-full flex justify-center" style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.8))' }}>
            <div className="w-1 h-3 bg-white rounded-full mt-2 opacity-90"></div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
